import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  markAdminSupportCaseReadInCache,
  prefetchAdminSupportCase,
  useSupportCaseManagement,
} from "@/hooks/useSupportCaseManagement";
import { BaseSupportCase, SupportCaseFilters } from "@/types/support";
import { Pagination } from "@/components/ui/pagination";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n";
import { AdminSupportPageContent } from "@/admin/pages/AdminSupportContent";

type SupportType = "all" | "complaint" | "service";
type StatusFilter =
  | "all"
  | "not_started"
  | "in_progress"
  | "resolved"
  | "closed";

export default function AdminSupport() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};

  const [selectedType, setSelectedType] = useState<SupportType>("all");
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);

  const itemsPerPage = 10;

  const filters: SupportCaseFilters & {
    page: number;
    limit: number;
    search?: string;
  } = {
    page: currentPage,
    limit: itemsPerPage,
    ...(selectedType !== "all" && { type: selectedType }),
    ...(selectedStatus !== "all" && { status: selectedStatus }),
    ...(isSearching && searchQuery && { search: searchQuery }),
  };

  const { supportCases, supportCasesMeta, isLoading, error } =
    useSupportCaseManagement(filters);

  const handleCaseClick = (case_: BaseSupportCase) => {
    markAdminSupportCaseReadInCache(queryClient, case_.id);
    void prefetchAdminSupportCase(queryClient, case_.id);
    navigate(`/admin/support/${case_.id}`);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 px-4">
        <div className="text-sm text-red-500 text-center">
          {L.errorLoadingGeneric ??
            "Kunde inte ladda supportärenden. Försök igen senare."}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-6 max-w-6xl">
      <div className="flex flex-col gap-2 mb-4 sm:mb-6">
        <BtnNavigateBack label="Dashboard" className="mb-0 self-start" />
        <h1 className="text-lg sm:text-2xl font-bold tracking-tight">
          {L.supportCases ?? "Supportärenden"}
        </h1>
      </div>

      <AdminSupportPageContent
        L={L}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedStatus={selectedStatus}
        handleStatusTabChange={(status) => {
          setSelectedStatus(status);
          setCurrentPage(1);
        }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearch={() => {
          setIsSearching(true);
          setCurrentPage(1);
        }}
        handleClearSearch={() => {
          setSearchQuery("");
          setIsSearching(false);
          setCurrentPage(1);
        }}
        isSearching={isSearching}
        isLoading={isLoading}
        supportCases={supportCases}
        handleCaseClick={handleCaseClick}
        supportCasesMeta={supportCasesMeta}
        currentPage={currentPage}
        handlePageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        Pagination={Pagination}
        handleItemsPerPageChange={() => {}}
      />
    </div>
  );
}
